import { Table } from 'rsuite';
import { Icon as MdiIcon } from '@mdi/react';
import { useTranslation } from 'react-i18next';
import { mdiLicense } from '@mdi/js';
import { Avatar } from '@material-ui/core';
import './ReportTable.scss';

const { Column, HeaderCell, Cell } = Table;

/**
 * @typedef {Object} NameCellProps
 * @property {EmployeePreferencesReport.IEmployeePreference} rowData
 *
 * @param { NameCellProps & import('rsuite').CellProps } input
*/
const NameCell = ({ rowData, ...props }) => (
    <Cell
        {...props}
    >
        {rowData.isAwarded ? (
            <>
                <MdiIcon
                    path={mdiLicense}
                    size={1}
                    color="#00862c"
                />
                <span className="report-table-awarded report-table-ellipsis-oveflow">{rowData.name}</span>
            </>
        )
            : <span className="report-table-unawarded">{rowData.name}</span>}
    </Cell>
);

/**
 * @param { NameCellProps & import('rsuite').CellProps }  input
 */
const BoldedCell = ({ rowData, ...props }) => (
    <Cell
        {...props}
    >
        <div className="report-table-bid-selection-container">
            {rowData.isAwarded ? (
                <span className="report-table-awarded report-table-ellipsis-oveflow">{rowData[props.dataKey]}</span>)
                : <span>{rowData[props.dataKey]}</span>}
        </div>
    </Cell>
);

/**
 * @param { NameCellProps & import('rsuite').CellProps }  input
 */
const BidSelectionCell = ({ rowData, ...props }) => {
    const bidSelection = `${rowData.awardedScheduleName} ${rowData.isAwarded && rowData.quantity > 1
        ? `(${rowData.scheduleTemplateName}` : ''}`;
    return (
        <Cell
            {...props}
        >
            {rowData.isAwarded
                ? (
                    <div className="report-table-bid-selection-container">
                        <span className="report-table-awarded report-table-ellipsis-oveflow">
                            {bidSelection}
                        </span>
                        {rowData.quantity > 1 && (
                            <>
                                <Avatar className="group-quantity-avatar">{rowData.quantity}</Avatar>
                                <span className="report-table-awarded">
                                    )
                                </span>
                            </>
                        )}
                    </div>
                )
                : (
                    <div className="report-table-bid-selection-container">
                        <span className="report-table-ellipsis-oveflow">
                            {rowData.scheduleTemplateName}
                        </span>
                        {rowData.quantity > 1 && <Avatar className="group-quantity-avatar">{rowData.quantity}</Avatar>}
                    </div>
                )}
        </Cell>
    );
};

/**
 * @typedef {'asc'|'desc'} SortType
 * @typedef {Object} ReportTableProps
 * @property {(dataKey: string, sortType?: SortType | undefined) => void} handleSortColumn
 * @property {string} sortColumn
 * @property {SortType} sortType
 * @property {ManagerAppData.IDataviewFilter[]} dataviewFields
 * @property {ManagerAppData.IEmployee[]} dataviewEmployeeData
 * @property {boolean} isRebid
 * @property {boolean} showScheduledDateAndTime
 *
 * @param { ReportTableProps & import('rsuite').TableProps } input
 */
function ReportTable({
    sortColumn, handleSortColumn, sortType, dataviewFields, dataviewEmployeeData, isRebid, showScheduledDateAndTime, ...rest
}) {
    const { t } = useTranslation('shiftBidding', { keyPrefix: 'common.reports.employeePreferences.columns' });

    /**
     * @param {string} col
     * @param {number} eId
     * @returns {string}
     */
    const getDataviewAttributeValue = (col, eId) => {
        const ee = dataviewEmployeeData.find((e) => e.employeeId === eId);
        const val = ee?.dataviewAttributes.find((x) => x.key === col);
        return val?.value || '';
    };

    return (
        <>
            <Table
                key="employee-preferences-report-table"
                sortColumn={sortColumn}
                sortType={sortType}
                onSortColumn={handleSortColumn}
                {...rest}
            >
                <Column
                    key="name"
                    sortable
                    resizable
                    width={150}
                >
                    <HeaderCell>{t('name')}</HeaderCell>
                    <NameCell dataKey="name" />
                </Column>
                <Column
                    key="rank"
                    sortable
                    resizable
                >
                    <HeaderCell>{t('rank')}</HeaderCell>
                    <BoldedCell dataKey="rank" />
                </Column>
                {showScheduledDateAndTime && (
                    <Column
                        width={300}
                        resizable
                        key="scheduledDateAndTime"
                    >
                        <HeaderCell>{t('scheduledDateAndTime')}</HeaderCell>
                        <Cell dataKey="scheduledDateAndTime" />
                    </Column>
                )}
                <Column
                    resizable
                    key="submittedOn"
                    width={150}
                >
                    <HeaderCell>{t('submittedOn')}</HeaderCell>
                    <Cell dataKey="submittedOn" />
                </Column>
                <Column
                    key="preference"
                    sortable
                    resizable
                >
                    <HeaderCell>{t('preference')}</HeaderCell>
                    <BoldedCell dataKey="preference" />
                </Column>
                {
                    dataviewFields
                        .filter((x) => x.checked)
                        .map((element) => (
                            <Column
                                key={element.key}
                                resizable
                                width={150}
                            >
                                <HeaderCell>{element.label}</HeaderCell>
                                <Cell>
                                    {
                                        (
                                            /** @type {EmployeePreferencesReport.IEmployeePreference} */rowData,
                                        ) => <span className={rowData.isAwarded ? 'report-table-awarded report-table-ellipsis-oveflow' : ''}>{getDataviewAttributeValue(element.key, rowData.employeeId)}</span>
                                    }
                                </Cell>
                            </Column>
                        ))
                }
                {isRebid && (
                    <Column
                        key="currentScheduleTemplateName"
                        sortable
                        resizable
                        width={200}
                    >
                        <HeaderCell>{t('currentShiftLineAssignment')}</HeaderCell>
                        <Cell dataKey="currentShiftLineAssignment" />
                    </Column>
                )}
                <Column
                    key="scheduleTemplateName"
                    sortable
                    resizable
                    width={200}
                >
                    <HeaderCell>{t('scheduleTemplateName')}</HeaderCell>
                    <BidSelectionCell dataKey="scheduleTemplateName" />
                </Column>
            </Table>
        </>
    );
}

export default ReportTable;
